import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import setup from '../../assets/data/setup.json';
import {
  LETTER_FORMATS,
  copyLetterToClipboard,
  downloadLetterAsDoc,
  downloadLetterAsText,
  extractTextFromFile,
  generateLetter,
  generateLetterWithAi,
  hasEnvOpenAiKey,
  hasOpenAiKey,
  parseJobDescription,
  resolveOpenAiKey,
  slugify,
} from '../../cover-letter-engine';
import { sanitizeHiringManager } from '../../cover-letter-engine/letterUtils';
import type { GeneratedLetter, JobDetails, LetterFormat, LetterTone } from '../../cover-letter-engine';
import '../resume/resume.scss';
import './cover-letter.scss';

function CoverLetterPage() {
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [hiringManager, setHiringManager] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [format, setFormat] = useState<LetterFormat>('cover_letter');
  const [tone, setTone] = useState<LetterTone>('professional');
  const [useAi, setUseAi] = useState(false);
  const [openAiKey, setOpenAiKey] = useState(() => sessionStorage.getItem('cover-letter-openai-key') || '');
  const [letter, setLetter] = useState<GeneratedLetter | null>(null);
  const [generating, setGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parsedPreview = useMemo(() => {
    if (!jobDescription.trim()) return null;
    return parseJobDescription(jobDescription);
  }, [jobDescription]);

  const buildJobDetails = useCallback((): JobDetails => {
    const parsed = jobDescription.trim() ? parseJobDescription(jobDescription) : null;
    return {
      company: company.trim() || parsed?.inferredCompany || '',
      role: role.trim() || parsed?.inferredRole || '',
      hiringManager: sanitizeHiringManager(hiringManager),
      jobDescription: jobDescription.trim(),
    };
  }, [company, role, hiringManager, jobDescription]);

  const handleGenerate = async () => {
    setError(null);
    const job = buildJobDetails();

    if (!job.jobDescription && !job.company && !job.role) {
      setError('Add a job description, or enter at least the company and role.');
      return;
    }

    setGenerating(true);
    try {
      if (openAiKey.trim()) sessionStorage.setItem('cover-letter-openai-key', openAiKey.trim());

      const apiKey = resolveOpenAiKey(openAiKey);
      const result =
        useAi && apiKey
          ? await generateLetterWithAi({ job, format, tone, useAi: true, openAiKey: apiKey })
          : generateLetter({ job, format, tone });

      setLetter(result);
    } catch {
      setError('Generation failed. Try again or disable AI enhancement.');
    } finally {
      setGenerating(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setExtracting(true);
    setError(null);
    try {
      const text = await extractTextFromFile(file);
      setJobDescription((prev) => (prev ? `${prev}\n\n${text}` : text));

      const parsed = parseJobDescription(text);
      if (!company && parsed.inferredCompany) setCompany(parsed.inferredCompany);
      if (!role && parsed.inferredRole) setRole(parsed.inferredRole);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read file.');
    } finally {
      setExtracting(false);
      event.target.value = '';
    }
  };

  const handleCopy = async () => {
    if (!letter) return;
    await copyLetterToClipboard(letter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = (type: 'txt' | 'doc') => {
    if (!letter) return;
    const base = slugify(`${setup.profile.name}-${role || company || format}`);
    if (type === 'txt') downloadLetterAsText(letter, `${base}.txt`);
    else downloadLetterAsDoc(letter, `${base}.doc`);
  };

  const hasEnvAiKey = hasEnvOpenAiKey();

  return (
    <div className="resume-page cover-letter-page">
      <div className="resume-toolbar no-print">
        <div className="resume-toolbar-left">
          <Link to="/" className="resume-toolbar-link">
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Portfolio
          </Link>
          <span className="resume-toolbar-title">Cover Letter Generator</span>
        </div>
        <div className="resume-toolbar-actions">
          <Link to="/resume" className="resume-btn resume-btn-secondary">
            Resume Generator
          </Link>
        </div>
      </div>

      <div className="cover-letter-layout">
        <aside className="cover-letter-form no-print">
          <p className="cover-letter-intro">
            Paste a job description or upload a PDF/screenshot. We&apos;ll match your portfolio data and draft a human, professional letter.
          </p>

          <label className="cover-letter-field">
            <span>Company</span>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="e.g. Acme Corp"
            />
          </label>

          <label className="cover-letter-field">
            <span>Job title</span>
            <input
              type="text"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Senior Full-Stack Engineer"
            />
          </label>

          <label className="cover-letter-field">
            <span>Hiring manager (optional)</span>
            <input
              type="text"
              value={hiringManager}
              onChange={(e) => setHiringManager(e.target.value)}
              placeholder="e.g. Jane Smith — name only, not an email"
            />
          </label>

          <label className="cover-letter-field">
            <span>Job description</span>
            <textarea
              rows={10}
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="Paste the full job posting here..."
            />
          </label>

          <label className="cover-letter-upload">
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,application/pdf,image/*,text/plain"
              onChange={handleFileUpload}
              disabled={extracting}
            />
            <UploadFileIcon sx={{ fontSize: 18 }} />
            {extracting ? 'Extracting text…' : 'Upload PDF or screenshot'}
          </label>

          {parsedPreview && parsedPreview.keywords.length > 0 && (
            <div className="cover-letter-keywords">
              <p className="cover-letter-keywords-label">Detected focus areas</p>
              <div className="cover-letter-keyword-list">
                {parsedPreview.keywords.slice(0, 10).map((kw) => (
                  <span key={kw} className="cover-letter-keyword">{kw}</span>
                ))}
              </div>
            </div>
          )}

          <div className="cover-letter-row">
            <label className="cover-letter-field">
              <span>Format</span>
              <select value={format} onChange={(e) => setFormat(e.target.value as LetterFormat)}>
                {LETTER_FORMATS.map((f) => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            </label>

            <label className="cover-letter-field">
              <span>Tone</span>
              <select value={tone} onChange={(e) => setTone(e.target.value as LetterTone)}>
                <option value="professional">Professional</option>
                <option value="warm">Warm</option>
                <option value="concise">Concise</option>
              </select>
            </label>
          </div>

          <details className="cover-letter-ai-details">
            <summary>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
              AI enhancement (optional)
            </summary>
            <p className="cover-letter-ai-note">
              {hasEnvAiKey
                ? 'Site API key loaded from REACT_APP_OPENAI_API_KEY. Leave the field below empty to use it, or paste a key to override for this session.'
                : 'Set REACT_APP_OPENAI_API_KEY in your .env file, or paste a key below. Without a key, smart templates from your portfolio data are used.'}
            </p>
            <label className="cover-letter-field">
              <span>OpenAI API key</span>
              <input
                type="password"
                value={openAiKey}
                onChange={(e) => setOpenAiKey(e.target.value)}
                placeholder={hasEnvAiKey ? 'Optional override' : 'sk-...'}
              />
            </label>
            <label className="cover-letter-checkbox">
              <input
                type="checkbox"
                checked={useAi}
                onChange={(e) => setUseAi(e.target.checked)}
                disabled={!hasOpenAiKey(openAiKey)}
              />
              Use AI to refine wording
            </label>
          </details>

          {error && <p className="cover-letter-error">{error}</p>}

          <button
            type="button"
            className="resume-btn cover-letter-generate"
            onClick={handleGenerate}
            disabled={generating || extracting}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </aside>

        <section className="cover-letter-preview">
          {!letter ? (
            <div className="cover-letter-empty">
              <p>Your letter will appear here.</p>
              <p className="cover-letter-empty-hint">
                Formats: application form (ATS portal), cover letter, outreach email, LinkedIn, follow-up, recruiter intro.
              </p>
            </div>
          ) : (
            <>
              <div className="cover-letter-preview-toolbar no-print">
                <button type="button" className="resume-btn resume-btn-secondary" onClick={handleGenerate} disabled={generating}>
                  <RefreshIcon sx={{ fontSize: 16 }} />
                  Regenerate
                </button>
                <button type="button" className="resume-btn resume-btn-secondary" onClick={handleCopy}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button type="button" className="resume-btn resume-btn-secondary" onClick={() => handleDownload('txt')}>
                  <DownloadIcon sx={{ fontSize: 16 }} />
                  .txt
                </button>
                <button type="button" className="resume-btn" onClick={() => handleDownload('doc')}>
                  <DownloadIcon sx={{ fontSize: 16 }} />
                  Word
                </button>
              </div>
              <article className="cover-letter-document">
                <pre className="cover-letter-text">{letter.fullText}</pre>
              </article>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default CoverLetterPage;
